// src/pages/PeakHours.jsx
import React, { useEffect, useState, useCallback, useContext } from "react";
import { Card, CardBody, Spinner, ButtonGroup, Button, Badge } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import { request } from "../../utils/request";
import { ColorModeContext } from "../../index";
import "./dashboard.scss";

/* days options */
const DAYS_OPTIONS = [7, 14];

/* same polish CSS injection used across other mini-cards (slightly extended for buttons) */
const injectMiniWidgetStyles = () => {
  if (document.getElementById("mini-widget-polish")) return;
  const css = `
    .mw-light-root { --bg: #fff; --tile-bg: #f9fafc; --muted: #666; --text: #0b1220; --tile-border: rgba(6,15,34,0.04); --icon-ghost: rgba(3,7,18,0.03); --shadow: 0 1px 2px rgba(0,0,0,0.06); }
    .mw-dark-root { --bg: #252b3b; --tile-bg: #2f3649; --muted: #c9c9c9; --text: #f5f5f5; --tile-border: rgba(255,255,255,0.05); --icon-ghost: rgba(255,255,255,0.03); --shadow: 0 1px 3px rgba(0,0,0,0.35); }

    .polish-card { border-radius: 12px; overflow: visible; position: relative; }
    .polish-card .polish-body { padding: 0.9rem; box-sizing: border-box; }
    .polish-header { display:flex; align-items:center; justify-content:space-between; gap:12px; margin-bottom:6px; }
    .polish-title { margin:0; font-size:1rem; font-weight:700; color:var(--text); letter-spacing:0.2px; }
    .polish-sub { margin:0; font-size:0.85rem; color:var(--muted); }
    .control-row { display:flex; gap:8px; align-items:center; }
    .view-pill { border-radius: 8px; padding:6px 10px; font-size:0.85rem; cursor:pointer; border:1px solid rgba(0,0,0,0.04); background: transparent; }
    .view-pill.active { box-shadow: 0 6px 20px rgba(11,20,37,0.08); transform: translateY(-2px); }
    .polish-chart-wrap { min-height:200px; display:flex; align-items:center; justify-content:center; }
    .mw-light-root .polish-card { background: linear-gradient(180deg, rgba(255,255,255,0.95), rgba(248,251,255,0.95)); border: 1px solid var(--tile-border); box-shadow: var(--shadow); }
    .mw-dark-root .polish-card { background: linear-gradient(180deg, rgba(255,255,255,0.02), rgba(0,0,0,0.02)); border: 1px solid var(--tile-border); box-shadow: var(--shadow); color: var(--text); }

    /* responsive tweaks */
    @media (max-width:720px) { .polish-header { flex-direction: column; align-items: flex-start; gap:6px; } .polish-sub { font-size: 0.8rem; } }
  `;
  const s = document.createElement("style");
  s.id = "mini-widget-polish";
  s.appendChild(document.createTextNode(css));
  document.head.appendChild(s);
};

/* normalize responses (handles array, {data: []}, {data:{docs:[]}}, {docs:[]}) */
function normalizeArrayResp(resp) {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (resp.data) {
    if (Array.isArray(resp.data)) return resp.data;
    if (Array.isArray(resp.data.docs)) return resp.data.docs;
  }
  if (Array.isArray(resp.docs)) return resp.docs;
  const firstArray = Object.values(resp).find((v) => Array.isArray(v));
  return Array.isArray(firstArray) ? firstArray : [];
}

/* build array ['00:00','01:00',...,'23:00'] */
function hourCategories() {
  return Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, "0")}:00`);
}

/* weekday labels Sun..Sat */
const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/* vivid palettes tuned for light/dark */
const PALETTES = {
  light: {
    primary: "#0f172a", // deep indigo (for text accents)
    bar: "#0ea5a4", // teal/aqua
    heat: [
      "#E6FBFF", // very light cyan
      "#9EEBEC", // soft aqua
      "#60C6FF", // bright sky
      "#7C5CFF", // vivid purple
      "#FF6B6B", // coral-red (hot)
    ],
    heatRanges: [
      { from: 1, to: 1, color: "#E6FBFF" },
      { from: 2, to: 3, color: "#9EEBEC" },
      { from: 4, to: 7, color: "#60C6FF" },
      { from: 8, to: 15, color: "#7C5CFF" },
      { from: 16, to: 99999, color: "#FF6B6B" },
    ],
  },
  dark: {
    primary: "#E6F0FF", // soft light for text accents
    bar: "#60D2B2", // mint teal
    heat: [
      "#052227", // deep teal-black (lowest)
      "#004e64",
      "#0b6b8a",
      "#6b46c1", // purple
      "#ff6b6b", // hot
    ],
    heatRanges: [
      { from: 1, to: 1, color: "#052227" },
      { from: 2, to: 3, color: "#004e64" },
      { from: 4, to: 7, color: "#0b6b8a" },
      { from: 8, to: 15, color: "#6b46c1" },
      { from: 16, to: 99999, color: "#ff6b6b" },
    ],
  },
};

export default function PeakHours() {
  const { mode } = useContext(ColorModeContext);
  const isDark = mode === "dark";

  useEffect(() => injectMiniWidgetStyles(), []);

  const [daysRange, setDaysRange] = useState(7);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState("hourly"); // 'hourly' | 'heatmap'

  // charts data
  const [hourCounts, setHourCounts] = useState(new Array(24).fill(0));
  const [heatmapSeries, setHeatmapSeries] = useState([]); // [{ name: 'Mon', data: [{x:'00:00', y:0}, ...] }, ...]
  const [activeHourCats, setActiveHourCats] = useState([]); // only hours that have any login
  const [fetchedAt, setFetchedAt] = useState(null);

  // make both charts the same height to match visuals
  const chartHeight = 240;

  const fetchData = useCallback(
    async (useRange = daysRange) => {
      setLoading(true);
      try {
        const resp = await request(`logs?page=1&limit=10000&eventType=login`, { method: "GET" }).catch(async () => {
          return await request(`logs?page=1&limit=10000`, { method: "GET" });
        });

        const logs = normalizeArrayResp(resp);
        const now = new Date();
        const start = new Date(now.getTime() - useRange * 24 * 60 * 60 * 1000);

        const hoursAgg = new Array(24).fill(0);
        const weekHour = Array.from({ length: 7 }, () => new Array(24).fill(0));

        for (const lg of logs) {
          if (!lg) continue;
          // robust timestamp extraction
          let ts =
            lg.timestamp ||
            lg.createdAt ||
            (lg.eventData && lg.eventData.data && lg.eventData.data.timestamp) ||
            lg.time ||
            (lg.eventData && lg.eventData.login && lg.eventData.login.createdAt) ||
            null;
          if (!ts && typeof lg === "string") ts = lg;
          if (!ts) continue;
          const d = new Date(ts);
          if (isNaN(d.getTime())) continue;
          if (d < start || d > now) continue;

          const hr = d.getHours();
          const wd = d.getDay(); // 0..6
          hoursAgg[hr] = (hoursAgg[hr] || 0) + 1;
          weekHour[wd][hr] = (weekHour[wd][hr] || 0) + 1;
        }

        setHourCounts(hoursAgg);

        // determine which hours have any activity
        const actIdx = [];
        for (let i = 0; i < 24; i++) {
          const totalForHour = weekHour.reduce((acc, row) => acc + (row[i] || 0), 0);
          if (totalForHour > 0) actIdx.push(i);
        }
        const activeCats = actIdx.map((i) => `${String(i).padStart(2, "0")}:00`);
        setActiveHourCats(activeCats);

        // build heatmap series using only active hours so columns align with hourly chart
        const heatSeries = WEEKDAY_LABELS.map((label, idx) => {
          const row = weekHour[idx] || new Array(24).fill(0);
          const data = actIdx.map((hour) => ({ x: `${String(hour).padStart(2, "0")}:00`, y: row[hour] || 0 }));
          return { name: label, data };
        });

        setHeatmapSeries(heatSeries);
        setFetchedAt(new Date());
      } catch (err) {
        console.error("PeakHours fetch error:", err);
        setHourCounts(new Array(24).fill(0));
        setHeatmapSeries([]);
        setActiveHourCats([]);
      } finally {
        setLoading(false);
      }
    },
    [daysRange]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData, daysRange]);

  // derive filtered bar categories + data (remove hours with zero logins)
  const activeIndices = activeHourCats.map((h) => parseInt(h.slice(0, 2), 10));
  const filteredBarCats = activeHourCats.length ? activeHourCats : [];
  const filteredBarData = activeHourCats.length ? activeIndices.map((i) => hourCounts[i] || 0) : [];

  // compute peak hour label & counts (for summary)
  let peakHourLabel = "—";
  let peakCount = 0;
  if (hourCounts.some((c) => c > 0)) {
    let max = -1;
    let idx = 0;
    hourCounts.forEach((c, i) => {
      if (c > max) {
        max = c;
        idx = i;
      }
    });
    peakHourLabel = `${String(idx).padStart(2, "0")}:00`;
    peakCount = max;
  }

  // palette selection
  const pal = isDark ? PALETTES.dark : PALETTES.light;

  // BAR chart options (filtered hours) with subtle gradient fill
  const barOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      animations: { enabled: true },
      foreColor: isDark ? "#E0E6ED" : "#475569",
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "55%",
        borderRadius: 6,
      },
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "vertical",
        shadeIntensity: 0.3,
        inverseColors: false,
        opacityFrom: 0.95,
        opacityTo: 0.9,
        stops: [0, 60, 100],
      },
    },
    dataLabels: { enabled: false },
    xaxis: {
      categories: filteredBarCats,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: { rotate: -45, hideOverlappingLabels: true, style: { fontSize: "11px" } },
    },
    yaxis: { title: { text: "Logins" } },
    tooltip: { y: { formatter: (v) => `${v} logins` } },
    colors: [pal.bar],
    grid: { borderColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)" },
  };
  const barSeries = [{ name: "Logins", data: filteredBarData }];

  // HEATMAP options (use same height) with vivid color scale
  const heatOptions = {
    chart: {
      height: chartHeight,
      type: "heatmap",
      toolbar: { show: false },
      foreColor: isDark ? "#E0E6ED" : "#475569",
    },
    dataLabels: { enabled: false },
    plotOptions: {
      heatmap: {
        shadeIntensity: 0.8,
        radius: 4,
        colorScale: {
          ranges: isDark ? PALETTES.dark.heatRanges : PALETTES.light.heatRanges,
        },
      },
    },
    tooltip: { y: { formatter: (v) => `${v} logins` } },
    legend: { show: true, position: "top" },
    grid: { borderColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)" },
  };

  // rootClass to reuse theme vars from injected CSS
  const rootClass = isDark ? "mw-dark-root" : "mw-light-root";

  return (
    <div className={`widget-card ${rootClass}`} style={{ width: "100%" }}>
      <Card
        className="polish-card"
        style={{
          height: "100%",
          backgroundColor: isDark ? "#252b3b" : "#fff",
          border: isDark ? "none" : "1px solid #e0e0e0",
          borderRadius: "12px",
        }}
      >
        <div className="card-sheen" style={{ position: "absolute", inset: 0, borderRadius: 12, pointerEvents: "none" }} />

        <CardBody className={isDark ? "text-white" : ""} style={{ padding: 0 }}>
          <div className="polish-header" style={{ padding: "0.9rem 0.9rem 0 0.9rem" }}>
            <div>
              <h5 className="polish-title" style={{ margin: 0 }}>
                Peak Login Hours
              </h5>
              <div className="polish-sub" style={{ marginTop: 6 }}>
                Last <strong>{daysRange} days</strong>. Peak:{" "}
                <strong style={{ color: pal.bar }}>{peakHourLabel}</strong>{" "}
                <small style={{ color: isDark ? "#9aa4b2" : "#6b7280" }}>({peakCount} logins)</small>
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {/* View toggle implemented as ButtonGroup to match Days toggle */}
              <ButtonGroup size="sm" aria-label="Chart view toggle">
                <Button color={viewMode === "hourly" ? "primary" : "secondary"} onClick={() => setViewMode("hourly")} active={viewMode === "hourly"}>
                  Hourly
                </Button>
                <Button color={viewMode === "heatmap" ? "primary" : "secondary"} onClick={() => setViewMode("heatmap")} active={viewMode === "heatmap"}>
                  Heatmap
                </Button>
              </ButtonGroup>

              {/* Days toggle */}
              <ButtonGroup size="sm" className="ms-2" aria-label="Days range">
                {DAYS_OPTIONS.map((d) => (
                  <Button key={d} color={daysRange === d ? "primary" : "secondary"} onClick={() => setDaysRange(d)} active={daysRange === d}>
                    {d}d
                  </Button>
                ))}
              </ButtonGroup>
            </div>
          </div>

          <div style={{ padding: "0 0.9rem 0.6rem 0.9rem" }}>
            {/* Chart area: use single chart area; both charts share same height for visual parity */}
            <div style={{ minHeight: chartHeight, transition: "all 200ms ease" }}>
              {loading ? (
                <div className="d-flex align-items-center justify-content-center" style={{ height: chartHeight }}>
                  <Spinner color={isDark ? "light" : "primary"} />
                </div>
              ) : viewMode === "hourly" ? (
                filteredBarCats.length === 0 ? (
                  <div className="d-flex align-items-center justify-content-center" style={{ height: chartHeight }}>
                    <div className={isDark ? "text-muted" : "text-secondary"}>No login events in selected range</div>
                  </div>
                ) : (
                  <ReactApexChart options={barOptions} series={barSeries} type="bar" height={chartHeight} />
                )
              ) : heatmapSeries.length === 0 || activeHourCats.length === 0 ? (
                <div className="d-flex align-items-center justify-content-center" style={{ height: chartHeight }}>
                  <div className={isDark ? "text-muted" : "text-secondary"}>No heatmap data</div>
                </div>
              ) : (
                <ReactApexChart options={heatOptions} series={heatmapSeries} type="heatmap" height={chartHeight} />
              )}
            </div>

            {/* Footer / meta */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
     

              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <Badge color="info" pill style={{ backgroundColor: isDark ? "#1f2a3a" : undefined }}>
                  Hours shown: {activeHourCats.length}
                </Badge>
                <Badge color="secondary" pill style={{ backgroundColor: isDark ? "#1b2430" : undefined }}>
                  Total logins: {hourCounts.reduce((a, b) => a + b, 0)}
                </Badge>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
